import { AnimatePresence, motion } from 'motion/react'
import { BarChart3, Check, Cookie, Settings, Shield, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import {
  CookieConsentManager,
  type CookiePreferences,
  initializeAnalytics,
} from '~/lib/utils/cookie_consent'

export default function CookieConsentBanner() {
  const [isVisible, setIsVisible] = useState(false)
  const [showSettings, setShowSettings] = useState(false)
  const [customPreferences, setCustomPreferences] = useState<CookiePreferences>({
    essential: true,
    analytics: false,
  })

  useEffect(() => {
    // Show banner if consent hasn't been given
    const hasConsent = CookieConsentManager.hasConsent()
    setIsVisible(!hasConsent)

    if (hasConsent) {
      // Initialize services based on existing preferences
      initializeAnalytics()
    }
  }, [])

  const handleAcceptAll = () => {
    CookieConsentManager.acceptAll()
    setIsVisible(false)

    // Initialize all services
    initializeAnalytics()
  }

  const handleAcceptEssentialOnly = () => {
    CookieConsentManager.rejectAll()
    setIsVisible(false)

    // Only initialize essential services (no analytics) - nothing to initialize for essential only
  }

  const handleCustomPreferences = (preferences: CookiePreferences) => {
    CookieConsentManager.setPreferences(preferences)
    setIsVisible(false)

    // Initialize services based on preferences
    if (preferences.analytics) {
      initializeAnalytics()
    }
  }

  const cookieTypes = [
    {
      key: 'essential' as keyof CookiePreferences,
      title: 'Essential Cookies',
      description: 'Required for authentication and basic site functionality',
      required: true,
      icon: Shield,
      examples: ['Session cookies', 'Authentication tokens'],
    },
    {
      key: 'analytics' as keyof CookiePreferences,
      title: 'Analytics Cookies',
      description: 'Helps us improving your experience by collecting usage and performance data',
      required: false,
      icon: BarChart3,
      examples: ['Usage tracking', 'Performance metrics'],
    },
  ]

  if (!isVisible) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-end justify-center p-4 sm:items-center">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/20 backdrop-blur-xs"
          onClick={() => !showSettings && setIsVisible(false)}
        />

        {/* Banner */}
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.9 }}
          className="relative w-full max-w-4xl"
        >
          <Card className="border-2 border-primary/20 bg-background shadow-2xl">
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-primary/10 p-2">
                    <Cookie className="size-6 text-primary" />
                  </div>
                  <CardTitle className="text-xl text-primary">Cookie Preferences</CardTitle>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsVisible(false)}
                  className="size-8 p-0"
                >
                  <X className="size-4" />
                </Button>
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              {!showSettings ? (
                <>
                  <p className="text-foreground">
                    We use essential cookies for authentication and basic functionality. We also
                    offer optional analytics cookies to help improve our service.
                  </p>

                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      onClick={handleAcceptAll}
                      className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      <Check className="mr-2 size-4" />
                      Accept All
                    </Button>
                    <Button
                      onClick={handleAcceptEssentialOnly}
                      variant="outline"
                      className="flex-1"
                    >
                      Essential Only
                    </Button>
                    <Button
                      onClick={() => setShowSettings(true)}
                      variant="outline"
                      className="flex-1"
                    >
                      <Settings className="mr-2 size-4" />
                      Customize
                    </Button>
                  </div>

                  <p className="text-center text-xs text-foreground">
                    By continuing to use our site, you agree to our{' '}
                    <a href="/website/policies" className="text-primary hover:underline">
                      Policies & Terms
                    </a>{' '}
                    and{' '}
                    <a href="/website/privacy" className="text-primary hover:underline">
                      Privacy Policy
                    </a>
                  </p>
                </>
              ) : (
                <>
                  <div>
                    <h3 className="mb-2 text-lg font-semibold text-primary">
                      Customize Cookie Preferences
                    </h3>
                    <p className="text-sm text-foreground">
                      Choose which cookies you want to accept. Essential cookies are always enabled
                      as they're required for the site to function.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {cookieTypes.map((type) => (
                      <div
                        key={type.key}
                        className={`rounded-lg border p-4 ${
                          type.required ? 'border-primary/20 bg-primary/5' : 'border-border'
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className="flex w-full items-start gap-3">
                            <div className="shrink-0">
                              <div className="rounded-lg bg-primary/10 p-2">
                                <type.icon className="size-4 text-primary" />
                              </div>
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-foreground">{type.title}</h4>
                                  {type.required && (
                                    <span className="rounded-full bg-primary px-2 py-0.5 text-xs text-primary-foreground">
                                      Required
                                    </span>
                                  )}
                                </div>
                                <Switch
                                  checked={type.required ? true : customPreferences[type.key]}
                                  disabled={type.required}
                                  onCheckedChange={(checked) => {
                                    if (!type.required) {
                                      setCustomPreferences((prev) => ({
                                        ...prev,
                                        [type.key]: checked,
                                      }))
                                    }
                                  }}
                                />
                              </div>
                              <p className="mt-1 text-sm text-foreground">{type.description}</p>
                              {type.examples && (
                                <div className="mt-2">
                                  <p className="text-xs font-medium text-foreground">Examples:</p>
                                  <ul className="mt-1 text-xs text-foreground">
                                    {type.examples.map((example, i) => (
                                      <li key={i}>• {example}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex flex-col gap-3 sm:flex-row">
                    <Button
                      onClick={() => handleCustomPreferences(customPreferences)}
                      className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
                    >
                      <Check className="mr-2 size-4" />
                      Save Preferences
                    </Button>
                    <Button
                      onClick={() => setShowSettings(false)}
                      variant="outline"
                      className="flex-1"
                    >
                      Back
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}
